import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  useCreateNotificamehubOfficial,
  useUpdateNotificamehubOfficial,
} from '@/hooks/useChannelConnections';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-handler';
import type {
  CreateNotificamehubOfficialChannelDto,
  UpdateNotificamehubOfficialChannelDto,
} from '@/types/channels';

// Masked value constant
const MASKED_VALUE = '********';

// Schema for create mode - all fields required
const createSchema = z.object({
  name: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  apiToken: z.string().min(1, 'API Token é obrigatório'),
  channelId: z.string().min(1, 'Channel ID é obrigatório'),
});

// Schema for edit mode - name required, apiToken and channelId optional (apiToken can be masked)
const editSchema = z.object({
  name: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  apiToken: z.string().optional(),
  channelId: z.string().min(1, 'Channel ID é obrigatório'),
});

type NotificamehubOfficialFormData = z.infer<typeof createSchema>;

interface NotificamehubOfficialFormProps {
  mode?: 'create' | 'edit';
  channelId?: string;
  initialValues?: Partial<NotificamehubOfficialFormData>;
  onSuccess: () => void;
  onCancel: () => void;
}

export function NotificamehubOfficialForm({
  mode = 'create',
  channelId,
  initialValues,
  onSuccess,
  onCancel,
}: NotificamehubOfficialFormProps) {
  const { mutate: createChannel, isLoading: isCreating, error: createError } =
    useCreateNotificamehubOfficial();
  const { mutate: updateChannel, isLoading: isUpdating, error: updateError } =
    useUpdateNotificamehubOfficial(channelId || '');
  const { success, error: showError } = useToast();

  // Track if API token was changed in edit mode
  const [apiTokenChanged, setApiTokenChanged] = useState(false);

  const isLoading = mode === 'create' ? isCreating : isUpdating;
  const error = mode === 'create' ? createError : updateError;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NotificamehubOfficialFormData>({
    resolver: zodResolver(mode === 'create' ? createSchema : editSchema),
    defaultValues:
      mode === 'edit'
        ? {
            name: initialValues?.name || '',
            channelId: initialValues?.channelId || '',
            apiToken: '', // Empty in edit mode - will show placeholder
          }
        : {
            name: '',
            channelId: '',
            apiToken: '',
          },
  });

  const onSubmit = (data: NotificamehubOfficialFormData) => {
    if (mode === 'create') {
      const dto: CreateNotificamehubOfficialChannelDto = {
        name: data.name,
        apiToken: data.apiToken,
        channelId: data.channelId,
      };

      createChannel(dto, {
        onSuccess: () => {
          success('Canal criado com sucesso!', { id: 'channel-create' });
          onSuccess();
        },
        onError: (err) => {
          const message = getErrorMessage(err, 'Erro ao criar canal');
          showError(message, { id: 'channel-create-error' });
        },
      });
    } else {
      // In edit mode
      const dto: UpdateNotificamehubOfficialChannelDto = {
        name: data.name,
        channelId: data.channelId,
      };

      // Handle API token - send masked value if not changed, actual value if changed
      if (apiTokenChanged && data.apiToken && data.apiToken.trim()) {
        dto.apiToken = data.apiToken; // User provided new token
      } else {
        dto.apiToken = MASKED_VALUE; // Keep existing token
      }

      updateChannel(dto, {
        onSuccess: () => {
          success('Canal atualizado com sucesso!', { id: `channel-update-${channelId}` });
          onSuccess();
        },
        onError: (err) => {
          const message = getErrorMessage(err, 'Erro ao atualizar canal');
          showError(message, { id: `channel-update-error-${channelId}` });
        },
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            {error instanceof Error
              ? error.message
              : mode === 'create'
                ? 'Erro ao criar canal'
                : 'Erro ao atualizar canal'}
          </AlertDescription>
        </Alert>
      )}

      {/* Name Field */}
      <div className="space-y-2">
        <Label htmlFor="name">Nome do Canal</Label>
        <Input
          id="name"
          placeholder="Ex: Suporte WhatsApp NotificameHub"
          {...register('name')}
          disabled={isLoading}
        />
        {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
      </div>

      {/* Channel ID Field */}
      <div className="space-y-2">
        <Label htmlFor="channelId">
          Channel ID
          <span className="text-muted-foreground text-xs ml-2">(ID do canal no NotificameHub)</span>
        </Label>
        <Input
          id="channelId"
          placeholder="Ex: d0ce9d05-445f-4d49-b2f8-1909aa0086af"
          autoComplete="off"
          {...register('channelId')}
          disabled={isLoading}
        />
        {errors.channelId && <p className="text-sm text-destructive">{errors.channelId.message}</p>}
      </div>

      {/* API Token Field */}
      <div className="space-y-2">
        <Label htmlFor="apiToken">
          API Token
          <span className="text-muted-foreground text-xs ml-2">
            {mode === 'create'
              ? '(X-Api-Token do NotificameHub)'
              : '(deixe em branco para manter o token atual)'}
          </span>
        </Label>
        <Input
          id="apiToken"
          type="password"
          placeholder={
            mode === 'create' ? '••••••••••••••••' : 'Deixe em branco para não alterar'
          }
          autoComplete="new-password"
          {...register('apiToken')}
          onChange={(e) => {
            if (mode === 'edit' && e.target.value) {
              setApiTokenChanged(true);
            }
          }}
          disabled={isLoading}
        />
        {errors.apiToken && <p className="text-sm text-destructive">{errors.apiToken.message}</p>}
        {mode === 'edit' && (
          <p className="text-xs text-muted-foreground">
            O API Token atual está protegido. Digite um novo token apenas se desejar alterá-lo.
          </p>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <Button type="submit" disabled={isLoading} className="flex-1">
          {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {mode === 'create' ? 'Criar Canal' : 'Atualizar Canal'}
        </Button>
      </div>
    </form>
  );
}
